import React, { useMemo } from "react";
import Countdown, { zeroPad } from "react-countdown";
import Heading from "@druva-apps/orion/lib/heading";
import Input from "@druva-apps/orion/lib/input";
import Form from "@druva-apps/orion/lib/form";
import FormGroup from "@druva-apps/orion/lib/formGroup";
import Popover from "@druva-apps/orion/lib/popover";
import Button from "@druva-apps/orion/lib/button";

import BackButton from "../BackButton";
import FormCardBody from "../FormCardBody";
import styles from "../FormContainer.module.scss";
import PasswordTooltip, { passwordSchema } from "../activation/PasswordTooltip";

const validationSchema = (Yup) =>
  Yup.object().shape({
    password: passwordSchema,
    repeatedPassword: Yup.string()
      .required("Required")
      .oneOf(
        [Yup.ref("password"), null],
        "New Password and Confirm password must match"
      ),
    formActive: Yup.bool().oneOf([true]),
  });

const LoginSetPasswordForm = ({
  email,
  submitLabel,
  onSubmit,
  onBack,
  expiredDate,
  sessionExpireLabel,
  codeExpiredMessage,
}) => {
  const countDownRenderer = useMemo(() => {
    const prefix = sessionExpireLabel;
    const completedLabel = codeExpiredMessage ?? "Session expired.";
    return ({ minutes, seconds, completed }) => {
      if (completed) {
        return (
          <span
            className="oui-small oui-text-danger"
            data-testid="login-setpw-expiration-lbl"
          >
            {completedLabel}
          </span>
        );
      }
      return (
        <>
          <span className="oui-small" data-testid="login-setpw-expiration-lbl">
            {prefix}{" "}
          </span>
          <span>
            {minutes}:{zeroPad(seconds)}
          </span>
        </>
      );
    };
  }, [sessionExpireLabel, codeExpiredMessage]);

  const initialValues = useMemo(
    () => ({
      password: "",
      repeatedPassword: "",
      formActive: true,
    }),
    []
  );

  return (
    <FormCardBody
      footer={
        <BackButton
          dataTestId="login-setpw-back-btn"
          onClick={onBack}
          label="Go to Login"
        />
      }
    >
      <Heading
        type="4"
        text="Reset Password"
        className="oui-font-weight-normal oui-mt-2 oui-mb-4 oui-text-center"
        dataTestId="login-setpw-title"
      />
      <Form
        validationSchema={validationSchema}
        withValidation
        initialValues={initialValues}
        onSubmit={onSubmit}
        className={styles.formContainer}
        renderChildren={(props) => (
          <>
            {email ? (
              <div
                className="oui-mb-3 oui-pt-2 oui-ml-2"
                data-testid="login-setpw-email"
              >
                {email}
              </div>
            ) : null}
            <FormGroup className={`${styles.input} oui-mb-0`}>
              <Popover
                type="uncontrolled"
                title="Password must have at least"
                placement="right"
                className={styles.popover}
                body={() => (
                  <PasswordTooltip password={props.values.password} />
                )}
              >
                <Input
                  data-testid="login-setpw-new-txt"
                  type="password"
                  name="password"
                  placeholder="New Password"
                  disabled={!props.values.formActive}
                  onFocus={() => {
                    props.setStatus({ focused: { password: true } });
                  }}
                  validationProps={props}
                  isInvalid={
                    (props.touched.password &&
                      props.errors.password === "Required") ||
                    props.errors.password === "Maximum 64 characters"
                  }
                  autoComplete="new-password"
                />
              </Popover>
              {props.status?.focused?.password &&
                (props.errors.password === undefined ||
                  !props.touched.password ||
                  !(
                    props.errors.password === "Required" ||
                    props.errors.password === "Maximum 64 characters"
                  )) && (
                  <span className="oui-small oui-text-secondary">
                    Maximum 64 characters (
                    {Math.max(0, 64 - (props.values.password?.length || 0))}{" "}
                    remaining)
                  </span>
                )}
            </FormGroup>
            <FormGroup className={`${styles.input} oui-mb-0`}>
              <Input
                type="password"
                name="repeatedPassword"
                placeholder="Confirm Password"
                disabled={!props.values.formActive}
                validationProps={props}
                isInvalid={
                  props.touched.repeatedPassword &&
                  props.errors.repeatedPassword !== undefined
                }
                data-testid="login-setpw-confirm-txt"
                autoComplete="new-password"
              />
            </FormGroup>
            <input className="oui-d-none" type="submit" />
            <Button
              label={submitLabel}
              dataTestId="login-setpw-submit-btn"
              size="lg"
              block
              type="primary"
              onClick={props.submitForm}
              className="oui-mb-2"
              disabled={
                !props.values.formActive ||
                !props.isValid ||
                !props.values.password
              }
            />
            {expiredDate != null ? (
              <div className="oui-mb-3">
                <Countdown
                  date={expiredDate}
                  renderer={countDownRenderer}
                  onComplete={() => {
                    props.setFieldValue("formActive", false);
                  }}
                />
              </div>
            ) : null}
          </>
        )}
      />
    </FormCardBody>
  );
};

LoginSetPasswordForm.defaultProps = {
  email: "",
  submitLabel: "Continue",
  expiredDate: null,
  sessionExpireLabel: "The session will expire in",
  codeExpiredMessage: null,
};

export default LoginSetPasswordForm;
